import { useQuery } from '@tanstack/react-query'
import { CheckIcon, CircleAlert } from 'lucide-react'
import { useShallow } from 'zustand/shallow'

import { Button } from '@/components/atoms/Button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/atoms/Dialog'
import { useScopedI18n } from '@/locales/client'
import productDetails from '@/services/productDetails'
import type { DialogTypes } from '@/stores/DialogsStore'
import useDialogsStore from '@/stores/DialogsStore'
import { $language } from '@/stores/Language'
import { useIncludedIn } from '@/util/getIncludedIn'

const DIALOG_ID: DialogTypes = 'partInfo'

export default function PartDetailsDialog() {
  const t = useScopedI18n('dialogs.partDetails')

  const { open, part } = useDialogsStore(
    useShallow(state => state.dialogs.partInfo),
  )
  const toggle = useDialogsStore(useShallow(state => state.toggle))
  const includedIn = useIncludedIn(part!)

  const { data, isLoading, isError, refetch } = useQuery({
    enabled: !!part,
    queryKey: [DIALOG_ID, (part?.RNumberSpecial ?? part?.RNumber)],
    staleTime: 60 * 60000, // m to ms
    queryFn: async () => {
      const response = await productDetails(part!.RNumberSpecial ?? part!.RNumber, $language.get())
      // ? Forces cache of img before displaying it
      if (response?.productData.thumbnailImage) await fetch(response?.productData.thumbnailImage, { cache: 'force-cache' })
      return response?.productData
    },
  })

  return (
    <Dialog open={open} onOpenChange={() => toggle(DIALOG_ID)}>
      <DialogContent className="flex h-fit min-h-[40vh] flex-col items-center gap-4 p-4 text-left md:gap-8 md:p-16">
        <DialogHeader className="mt-8 w-full justify-center gap-0  space-y-0 text-center md:mt-2">
          <DialogTitle className="text-pretty text-center font-inter text-2xl font-bold leading-snug text-rsf-black md:text-left md:text-[32px]">
            {part?.partname}
          </DialogTitle>

          {data && (
            <p className="text-balance break-words font-inter text-lg font-normal leading-tight text-rsf-black md:text-xl">
              {data.ShortForm}
            </p>
          )}
        </DialogHeader>

        {isLoading && (
          <div
            className="my-auto grid h-full place-items-center py-8 font-sans"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <div className="loader size-7" aria-hidden></div>
            <span className="mt-2 text-rsf-black">{t('loading')}</span>
          </div>
        )}

        {isError && (
          <div className="my-auto grid h-full place-items-center py-8 font-sans" role="alert" aria-live="assertive">
            <CircleAlert className="mb-1 size-8 text-rsf-red" aria-hidden="true" />
            <h4 className="mb-2 text-lg text-rsf-black">{t('error.title')}</h4>
            <p className="mb-6 text-pretty break-words text-center leading-snug text-rsf-grey-dark">
              {t('error.description')}
            </p>
            <div className="flex flex-row gap-2">
              <Button variant="secondary" asChild>
                <a href="https://g1.redseafish.com/support/" target="_blank" rel="noreferrer noopener">
                  {t('error.contact')}
                </a>
              </Button>

              <Button onClick={() => refetch()}>
                {t('error.retry')}
              </Button>
            </div>
          </div>
        )}

        {!isLoading && data && (
          <>
            {data?.thumbnailImage && (
              <img
                src={data.thumbnailImage.replace('/320/', '/640x/')} // ? double size and auto width
                alt={data.ShortForm ?? ''}
                width={670}
                height={332}
                className="aspect-square h-[332px] w-full object-contain"
              />
            )}

            <div className="w-full text-balance break-words font-sans text-sm font-normal leading-5 text-rsf-black transition-all duration-700 fade-in md:text-base">
              {data.product_description && (
                <>
                  {/* eslint-disable-next-line react-dom/no-dangerously-set-innerhtml */}
                  <div dangerouslySetInnerHTML={{ __html: data.product_description }}></div>
                </>
              )}
            </div>

            {includedIn.length > 0 && (
              <div className="mb-1 flex w-full flex-row gap-2 border-t border-t-rsf-grey-light pt-4 font-sans">
                <div className="flex h-fit flex-row items-center gap-2">
                  <CheckIcon className="size-4 text-rsf-green" />
                  <p className="whitespace-nowrap text-sm font-bold text-rsf-black">{t('includedIn')}</p>
                </div>
                <p className="break-words text-sm capitalize leading-relaxed text-rsf-black">
                  {includedIn}
                </p>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
